import React, { useState, useEffect } from 'react';
import { Spin, Empty, Table, Button, Modal, Form, Input, DatePicker, Space, Alert, Switch, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { baseUrl_SPB } from '../../../BaseUrl';
import axios from 'axios';
import dayjs from 'dayjs';

const TimesheetPanel = ({ employeeId, token, isUserManager }) => {
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [allowances, setAllowances] = useState([]);

  useEffect(() => {
    fetchTimesheets();
  }, [employeeId]);

  const fetchTimesheets = async () => {
    if (!token || !employeeId) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(
        
        `${baseUrl_SPB}/api/v1/rostertimeallocation/gettimeallocationsbyuser?staffId=${employeeId}&status=timesheet`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        const tsData = data.filter((item) => item.status === 'timesheet');
        setTimesheets(Array.isArray(tsData) ? tsData : []);
      }
    } catch (err) {
      console.error('Timesheet endpoint unavailable');
      setTimesheets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTimesheet = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setError('');

      const payload = {
        staffId: employeeId,
        date: values.date.format('DD-MM-YYYY'),
        starttime: values.startTime?.format('HH:mm') || '00:00',
        endtime: values.endTime?.format('HH:mm') || '23:59',
        includeWeekends: values.includeWeekends || false,
        status: 'timesheet',
        timeAllocationAllowances: allowances,
      };

      await axios.post(`${baseUrl_SPB}/api/v1/timesheet/save`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setModalOpen(false);
      form.resetFields();
      setAllowances([]);
      await fetchTimesheets();
    } catch (err) {
      if (err?.errorFields) return;
      setError('Failed to save timesheet.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTimesheet = async (id) => {
    Modal.confirm({
      title: 'Delete timesheet entry',
      content: 'Are you sure?',
      okText: 'Delete',
      cancelText: 'Cancel',
      danger: true,
      onOk: async () => {
        try {
          await axios.delete(`${baseUrl_SPB}/api/v1/timesheet/delete/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          await fetchTimesheets();
        } catch (err) {
          setError('Failed to delete timesheet entry.');
          console.error(err);
        }
      },
    });
  };

  const getStatusColor = (status) => {
    if (status === 'Approved') return 'green';
    if (status === 'Pending') return 'orange';
    return 'default';
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Time',
      key: 'time',
      render: (_, record) => `${record.starttime} - ${record.endtime}`,
    },
    {
      title: 'Status',
      dataIndex: 'approvalStatus',
      key: 'approvalStatus',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status || 'Pending'}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteTimesheet(record.id)}
        />
      ),
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={12}>
      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} />}
      
      {isUserManager && (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
          Add timesheet entry
        </Button>
      )}

      <Spin spinning={loading}>
        {timesheets.length === 0 ? (
          <Empty description="No timesheet entries" />
        ) : (
          <Table
            rowKey={(rec) => rec.id || rec.date}
            dataSource={timesheets}
            columns={columns}
            pagination={false}
            size="small"
          />
        )}
      </Spin>

      <Modal
        open={modalOpen}
        title="Add Timesheet Entry"
        okText="Save"
        cancelText="Cancel"
        onOk={handleAddTimesheet}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
          setAllowances([]);
        }}
        confirmLoading={saving}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true, message: 'Required' }]}
          >
            <DatePicker format="DD-MM-YYYY" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="startTime"
            label="Start time"
            rules={[{ required: true, message: 'Required' }]}
          >
            <DatePicker picker="time" format="HH:mm" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="endTime"
            label="End time"
            rules={[{ required: true, message: 'Required' }]}
          >
            <DatePicker picker="time" format="HH:mm" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="includeWeekends"
            label="Include weekends"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <div style={{ marginBottom: 12 }}>
              <strong>Time Allocation Allowances</strong>
              <Button
                type="link"
                size="small"
                onClick={() => setAllowances([...allowances, { allowanceType: '', unit: '', value: 0 }])}
              >
                + Add allowance
              </Button>
            </div>

            {allowances.map((allowance, index) => (
              <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 40px', gap: 8, marginBottom: 8 }}>
                <Input
                  placeholder="Allowance type"
                  value={allowance.allowanceType}
                  onChange={(e) => {
                    const updated = [...allowances];
                    updated[index].allowanceType = e.target.value;
                    setAllowances(updated);
                  }}
                  size="small"
                />
                <Input
                  placeholder="Unit"
                  value={allowance.unit}
                  onChange={(e) => {
                    const updated = [...allowances];
                    updated[index].unit = e.target.value;
                    setAllowances(updated);
                  }}
                  size="small"
                />
                <Input
                  type="number"
                  placeholder="Value"
                  value={allowance.value}
                  onChange={(e) => {
                    const updated = [...allowances];
                    updated[index].value = parseFloat(e.target.value);
                    setAllowances(updated);
                  }}
                  size="small"
                />
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => setAllowances(allowances.filter((_, i) => i !== index))}
                />
              </div>
            ))}
          </div>
        </Form>
      </Modal>
    </Space>
  );
};

export default TimesheetPanel;
